export const meta = {
  title: 'Building a Unified Design Culture for CreateFuture',
  subtitle: 'Leading design through a company-wide rebrand, scaling a team of 8 and building a culture that could sustain itself without me in the room.',
  client: 'CreateFuture',
  role: 'UX & Design Manager, then Design Lead, Creative Studio',
  timeline: 'March 2022 to Nov 2025 (3.8 years)',
  services: 'Design Leadership, Brand Strategy, Rebrand'
};
export const heroStats = [{
  value: '100%',
  label: "Team retention through the\nmost disruptive period in company history"
}, {
  value: '8',
  label: "Multidisciplinary designers\nled as UX & Design Manager"
}, {
  value: '1,000+',
  label: 'Assets rebranded across every client-facing touchpoint'
}, {
  value: '2',
  label: "Competency frameworks\nbuilt from scratch"
}];
export const contextParagraphs = ["I joined what is now CreateFuture when the business was still xDesign, I was brought in as a UX & Design Manager to help define and establish the company's Product proposition while building the design team, leadership capability and culture needed to support its growth. Over the following years, as xDesign evolved into CreateFuture, I led the Creative Studio, shaping the company's creative strategy, brand and digital experiences. The Creative Studio supported a business of more than 500 employees, I fostered a high-performing design culture, introduced scalable design practices and strengthened collaboration across Product, Engineering, Marketing, and Leadership. Through this work, I helped raise the quality and consistency of design, improve delivery and create more cohesive customer and employee experiences across the organisation.", "Then came the acquisition.\nxDesign became CreateFuture. This wasn’t a just a visual refresh. 500+ employees had to unlearn one identity and adopt another, clients had to be convinced the new name carried the same track record and someone had to own that end to end. That someone was me."];
export const phases = [{
  tag: 'Phase 1',
  title: 'UX & Design Manager',
  split: 'Mostly leading rather than designing',
  headline: '8 designers',
  headlineLabel: 'Multidisciplinary team, direct reports',
  points: ['Led a multidisciplinary team of 8 designers across product, brand and research.', 'Built 2 competency frameworks, one for Product Design and one for Brand, giving the organisation a shared language for growth and progression for the first time.', 'Ran design critique sessions and open drop-in diary sessions.', 'Maintained 100% retention and supported multiple promotions through direct mentorship.']
}, {
  tag: 'Phase 2',
  title: 'Design Lead, Creative Studio',
  split: "80% design, 20% leadership",
  headline: '2 designers',
  headlineLabel: 'Direct reports, IC-leaning role owning the rebrand',
  points: ['Moved into a more hands-on, IC-leaning role with 2 designers reporting to me directly rather than a full team.', 'Owned the brand across every external touchpoint including digital platforms, e-commerce and B2B comms.', 'Staying hands-on kept me close to the day-to-day problems and kept my guidance grounded in current practice rather than outdated methods.', 'Working in the same tools and constraints as the team meant sharper, more specific feedback.']
}];
export const tensions = [{
  title: 'Speed versus quality',
  body: 'The business needed to move fast, but a rushed rebrand erodes the exact trust you’re trying to build.'
}, {
  title: 'Consistency versus flexibility',
  body: 'Marketing, Product, HR and Client Services all needed different things from the same system.'
}, {
  title: 'Team capacity versus demand',
  body: 'We couldn’t manually rebrand everything, so non-designers had to be able to use the system without breaking it.'
}];
export const approach = [{
  step: '1',
  title: 'Discovery',
  body: 'Audited the full asset library, mapped every client-facing touchpoint and aligned stakeholders across leadership, marketing and client services.'
}, {
  step: '2',
  title: 'Building the system',
  body: "Master templates in Figma and presentation tooling, brand governance grounded in real examples rather than abstract rules and AI tooling introduced to speed up production.",
  note: 'I hosted weekly drop-in brand training sessions and hands-on design workshops, as well as introducing AI tools (including Beautiful.ai for slide decks and documents) to accelerate production without sacrificing quality, a genuine innovation for the team.'
}, {
  step: '3',
  title: 'Execution at scale',
  body: 'Over 1,000 assets rebranded, with open drop-in sessions to unblock the wider team as they hit edge cases.'
}, {
  step: '4',
  title: 'Embedding',
  body: 'Governance for self-serve asset creation, plus training sessions and workshops run across the organisation.'
}];
export const collaborators = [{
  title: 'Product & Engineering',
  body: 'Aligned brand and product surfaces so the new identity held up in live software, not just marketing collateral.'
}, {
  title: 'Marketing & Growth',
  body: 'Built the campaign and asset pipeline together, planning brand work against the quarterly calendar rather than reacting to it.'
}, {
  title: 'People & Leadership',
  body: 'Competency frameworks, progression conversations and change communication with the C-suite and Heads of Department.'
}, {
  title: 'Clients & External',
  body: 'Protected client trust through the name change across proposals, pitch material, events and B2B comms.'
}];
export const outcomes = [{
  heading: 'For the team',
  items: ['Clear career pathways through 2 competency frameworks', 'Self-serve brand tools that non-designers could use safely', '500+ employees supported through the identity change', '100% retention, with multiple promotions']
}, {
  heading: 'For the business',
  items: ['A successful brand launch that maintained client trust', 'Successful marketing campaign with an ROI of over £1million', 'A scalable design system across every touchpoint', 'AI tooling that measurably reduced production time']
}];
export const rangeProjects = [{
  kicker: 'Sole Lead Designer',
  title: 'Catalyst24',
  body: 'Built the entire brand from scratch in one month covering identity, motion, design system and a responsive site in ReadyMag.',
  stat: '+67%',
  statLabel: 'Ticket sales, with satisfaction up from 3.2 to 4.8 / 5'
}];
export const reflections = [{
  title: 'More space for the emotional side of change',
  body: 'We moved quickly out of necessity. I’d invest more deliberately in helping the team process the identity shift, not just the practical side.'
}, {
  title: 'Clearer success metrics from the start',
  body: 'I’d set up adoption and brand confidence measures alongside delivery milestones rather than after.'
}, {
  title: 'More collaborative framework development',
  body: 'I wrote the competency frameworks largely solo then consulted. I’d involve more designers from the start next time.'
}, {
  title: 'More interactive motion and video output',
  body: 'The rebrand system leaned heavily on static templates and documents. With more runway I’d have built motion and video in as a core part of the system rather than an add-on.'
}];
export const testimonials = [{
  quote: 'Nek is able to combine clear strategic direction with trust and autonomy, giving the team room to do their best work while never losing sight of the bigger picture.',
  attribution: 'Colleague, CreateFuture'
}, {
  quote: 'Nektaria is dependable, a great listener and always willing to go above and beyond for the team and the work.',
  attribution: 'Colleague, CreateFuture'
}];
export const signOff = [{
  value: '100%',
  label: "RETENTION IN 1ST YEAR"
}, {
  value: '8 / 2',
  label: 'Designers led'
}, {
  value: '1,000+',
  label: 'Assets rebranded'
}, {
  value: "2",
  label: "COMPETENCY FRAMEWORKS"
}];